import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { EventId } from '@interfaces';
import { AccountTypes, FlowTypes, RejectedAccount, urlMapper } from '@interfaces/models';
import { select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import {
  closeSpinner,
  Go,
  LogError,
  LogInfo,
  selectItMultiReceivingToggle,
  State,
  TrackProcessError,
  UpdateDisplayedAccounts,
} from '@store/index';
import { selectMultiInternalTransferEligibility } from '@store/internal-transfers/selectors/eligibility/eligibility.selectors';
import { selectClientAccountsLoaded } from '@store/selectors/client-accounts/client-accounts.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, filter, map, Observable, of, switchMap, take, withLatestFrom } from 'rxjs';
@Injectable({ providedIn: 'root' })
export class DivorceEligibilityMultiItGuard implements CanActivate {
  constructor(
    private globalStore: Store<State>,
    private cookieService: CookieService,
  ) {}
  canActivate(): Observable<boolean> {
    // Considering the guard to behave singleton, resetting rejectedFromAccounts every canActivate call.
    let rejectedFromAccounts: RejectedAccount[] = [];
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }
    return this.globalStore.pipe(
      // Ensure that the client accounts are loaded before proceeding with eligibility check
      select(selectClientAccountsLoaded),
      filter((loaded) => loaded),
      take(1),
      withLatestFrom(
        this.globalStore.select(selectItMultiReceivingToggle),
        this.globalStore.select(selectMultiInternalTransferEligibility),
      ),
      switchMap(([, isMultiReceivingToggleOn, eligibilityResultIT]) => {
        // Skip eligibility guard flow if multi receiving is OFF
        if (!isMultiReceivingToggleOn) return of(true);

        // Minimum 1 IRA eligible account should be there in from accounts to proceed
        const isIraItEligible: boolean = eligibilityResultIT.isInternalTransferEligible;
        // Eligible accounts
        const accounts = eligibilityResultIT.fromAccounts;
        // From/To account types stays "IRA" in divorce.
        const accountFromType = eligibilityResultIT.isInternalTransferEligible
          ? eligibilityResultIT.transferFromType
          : eligibilityResultIT.ineligibleFromAccountType;
        const accountToType = AccountTypes.IRA;

        // If there are ineligible accounts, capture them in rejectedFromAccounts for logging later
        rejectedFromAccounts = eligibilityResultIT.rejectedFromAccounts || [];

        // If the client is eligible, update display accounts + log + close loading spinner and then activate page
        if (isIraItEligible) {
          // If from accounts are not available even though it says IT is eligible, throw error and route to error page
          if (!accounts) {
            throw new Error('BAD_PAYLOAD_FROM_ACCOUNTS');
          }
          // Dispatching eligible accounts to be displayed in the account selection page
          this.globalStore.dispatch(
            UpdateDisplayedAccounts({
              payload: accounts,
            }),
          );
          this.globalStore.dispatch(
            LogInfo({
              payload: {
                eventId: 'divorce-multi-it-guard--eligible-accounts-found',
                message: `Divorce Multi IT Eligibility Guard eligibility success ${FlowTypes.DIVORCE} ${accountFromType} account`,
                eligibleFromType: accountFromType,
                code: LoggerCodes.GENERAL_SUCCESS,
              },
            }),
          );
          this.globalStore.dispatch(closeSpinner());
          this.logIneligibleAccounts(
            'INELIGIBLE_ACCOUNTS_FROM',
            FlowTypes.DIVORCE,
            accountFromType,
            accountToType,
            rejectedFromAccounts,
          );
          // If the client is ineligible, go to the ineligible page along with logging/tracking error
        } else {
          this.globalStore.dispatch(Go({ path: [`${urlMapper[FlowTypes.DIVORCE]}/ineligible`] }));

          const message = `No eligible Divorce ${accountFromType} FROM account`;

          this.globalStore.dispatch(
            TrackProcessError({
              errorCode: `coo-divorce-ineligible-from-${accountFromType}_account`,
            }),
          );

          this.globalStore.dispatch(
            LogError({
              payload: {
                eventId: EventId.INELIGIBLE_PAGE_ROUTE,
                message,
                ineligibleFromAccountType: accountFromType,
                ineligibleToAccountType: accountToType,
                flowType: FlowTypes.DIVORCE,
                rejectedFromAccounts: this.maskRejectedAccountsPII(rejectedFromAccounts),
              },
            }),
          );
        }

        return of(isIraItEligible);
      }),
      catchError((err) => {
        this.globalStore.dispatch(Go({ path: ['error'] }));
        this.globalStore.dispatch(
          LogError({
            payload: {
              eventId: 'divorce-eligibility-multi-it-guard--unknown-error',
              message: `DivorceEligibilityMultiItGuard failed due to unknown error. ${err?.message ?? String(err)}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        );
        this.globalStore.dispatch(
          TrackProcessError({
            errorCode: `coo-divorce-ineligible-app_error`,
          }),
        );

        return of(false);
      }),
    );
  }

  private maskRejectedAccountsPII(
    rejectedAccounts: RejectedAccount[],
  ): Pick<RejectedAccount, 'accountNumber' | 'accountType' | 'rejectionReason'>[] {
    return rejectedAccounts.map((account) => ({
      accountNumber: account.accountNumber.slice(-4).padStart(account.accountNumber.length, '*'),
      accountType: account.accountType,
      rejectionReason: account.rejectionReason,
    }));
  }

  private logIneligibleAccounts(
    eventId: string,
    flowType: FlowTypes,
    selectedFromType: AccountTypes | null | undefined,
    selectedToType: AccountTypes | null,
    rejectedAccounts: RejectedAccount[],
  ): void {
    if (rejectedAccounts.length === 0) return;

    const batchSize = 200;
    const totalRejected = rejectedAccounts.length;
    const totalBatches = Math.ceil(totalRejected / batchSize);
    const maskedAccounts = this.maskRejectedAccountsPII(rejectedAccounts);

    for (let i = 0; i < totalBatches; i++) {
      this.globalStore.dispatch(
        LogInfo({
          payload: {
            eventId,
            message: `Ineligible accounts logged for ${eventId}`,
            flowType,
            code: LoggerCodes.GENERAL_SUCCESS,
            data: {
              selectedFromType: selectedFromType || '',
              selectedToType: selectedToType || '',
              totalRejectedCount: totalRejected,
              batchNumber: i + 1,
              totalBatches,
              rejectedAccounts: maskedAccounts.slice(i * batchSize, (i + 1) * batchSize),
            },
          },
        }),
      );
    }
  }
}
